import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import ActionButtonRow from './ui/ActionButtonRow'
import EmptyState from './ui/EmptyState'
import ErrorState from './ui/ErrorState'
import FilterBar from './ui/FilterBar'
import LoadingState from './ui/LoadingState'
import {
  TemplateFormShell,
  TemplateHeaderBlock,
  TemplatePage,
  TemplateSectionCard,
  TemplateSummaryRow,
  TemplateTableShell
} from './ui/PageTemplate'
import StatPill from './ui/StatPill'
import StatusBadge from './ui/StatusBadge'
import { formatPlainText, formatTitleText } from './utils/displayText'
import { buildSchemaUnavailableMessage, isSchemaMismatchError } from './admin/schemaGuard'
import { formatDateTime } from './utils/dateFormatting'
import { toPriorityStatus } from './utils/statusFormatting'
// Phase 3B3: Form hooks
import { useForm } from './hooks/useForm'
import { useFormMessage } from './hooks/useFormMessage'
import { useFormSubmit } from './hooks/useFormSubmit'

const createBlankForm = () => ({
  title: '',
  body: '',
  audience: 'team',
  site_id: '',
  priority: 'normal',
  is_active: true
})

const AUDIENCE_OPTIONS = [
  { value: 'team', label: 'Team-wide' },
  { value: 'site', label: 'Site-specific' },
  { value: 'urgent', label: 'Urgent audience' }
]

const PRIORITY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'important', label: 'Important' },
  { value: 'urgent', label: 'Urgent' }
]



export default function AnnouncementsCenter() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [unavailableMessage, setUnavailableMessage] = useState('')
  const [announcements, setAnnouncements] = useState([])

  // Phase 3B3: Replace manual form state with hooks
  const form = useForm({ initialValues: createBlankForm() })
  const formMessage = useFormMessage()
  const formSubmit = useFormSubmit({
    onSubmit: async (values) => {
      if (unavailableMessage) {
        throw new Error(unavailableMessage)
      }

      const title = formatTitleText(values.title)
      if (!title) {
        throw new Error('Announcement title is required.')
      }

      const payload = {
        title,
        body: formatPlainText(values.body),
        audience: String(values.audience || 'team').toLowerCase(),
        site_id: formatPlainText(values.site_id) || null,
        priority: String(values.priority || 'normal').toLowerCase(),
        is_active: values.is_active !== false,
        is_urgent: values.priority === 'urgent'
      }

      const response = await supabase.from('announcements').insert([payload])
      if (response.error) {
        throw new Error('Unable to save announcement: ' + response.error.message)
      }

      return { title }
    },
    onSuccess: async (result) => {
      form.reset()
      formMessage.setSuccess('Announcement published.')
      setComposerOpen(false)
      await load()
    },
    onError: (error) => {
      formMessage.setError(error.message)
    }
  })

  const [toggleBusyId, setToggleBusyId] = useState('')
  const [composerOpen, setComposerOpen] = useState(true)
  const [audienceFilter, setAudienceFilter] = useState('all')
  const [activityFilter, setActivityFilter] = useState('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    setUnavailableMessage('')

    const response = await supabase
      .from('announcements')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200)

    if (response.error) {
      if (isSchemaMismatchError(response.error)) {
        setAnnouncements([])
        setUnavailableMessage(buildSchemaUnavailableMessage('Announcements'))
        setLoading(false)
        return
      }

      setAnnouncements([])
      setError(response.error.message || 'Unable to load announcements.')
      setLoading(false)
      return
    }

    setAnnouncements(response.data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleSubmit = async (event) => {
    event.preventDefault()
    await formSubmit.submit(form.values)
  }

  const handleToggleActive = async (announcement) => {
    if (!announcement?.id) return
    if (unavailableMessage) {
      formMessage.setError(unavailableMessage)
      return
    }

    setToggleBusyId(String(announcement.id))
    formMessage.clear()

    const response = await supabase
      .from('announcements')
      .update({ is_active: !(announcement.is_active !== false) })
      .eq('id', announcement.id)

    if (response.error) {
      setToggleBusyId('')
      formMessage.setError('Unable to update announcement: ' + response.error.message)
      return
    }

    setToggleBusyId('')
    await load()
  }

  const stats = useMemo(() => {
    const total = announcements.length
    const active = announcements.filter((announcement) => announcement.is_active !== false).length
    const urgent = announcements.filter((announcement) => String(announcement.priority || '').toLowerCase() === 'urgent' || announcement.is_urgent === true).length
    return { total, active, urgent }
  }, [announcements])

  const visibleAnnouncements = useMemo(() => {
    return announcements.filter((announcement) => {
      const audience = String(announcement.audience || 'team').toLowerCase()
      const isActive = announcement.is_active !== false
      if (audienceFilter !== 'all' && audience !== audienceFilter) return false
      if (activityFilter === 'active' && !isActive) return false
      if (activityFilter === 'inactive' && isActive) return false
      return true
    })
  }, [activityFilter, announcements, audienceFilter])

  if (loading) return <LoadingState title="Loading announcements" message="Preparing team/site notices." />

  if (error) {
    return (
      <ErrorState
        title="Announcements unavailable"
        message={error}
        action={(
          <button type="button" className="gg-button gg-button--secondary" onClick={() => load()}>
            Retry
          </button>
        )}
      />
    )
  }

  return (
    <TemplatePage className="gg-annc-center gg-announcements-canonical" data-testid="announcements-center">
      <TemplateHeaderBlock
        eyebrow="Communications"
        title="Announcements"
        description="Publish team-wide, site-specific, and urgent notices from one consistent workspace."
        actions={(
          <ActionButtonRow>
            <button
              type="button"
              className="gg-button gg-button--secondary"
              onClick={() => load()}
              disabled={loading || Boolean(toggleBusyId)}
            >
              Refresh
            </button>
            <button type="button" className="gg-button gg-button--primary" onClick={() => setComposerOpen((open) => !open)}>
              {composerOpen ? 'Hide Composer' : 'New Announcement'}
            </button>
          </ActionButtonRow>
        )}
      >
        <TemplateSummaryRow>
          <article className="gg-card">
            <p className="gg-card__title">Total Notices</p>
            <p className="gg-card__meta">{stats.total}</p>
            <StatPill label="In view" value={visibleAnnouncements.length} tone="info" />
          </article>
          <article className="gg-card">
            <p className="gg-card__title">Active</p>
            <p className="gg-card__meta">{stats.active}</p>
            <StatPill label="Inactive" value={Math.max(0, stats.total - stats.active)} tone={stats.total - stats.active > 0 ? 'warning' : 'success'} />
          </article>
          <article className="gg-card">
            <p className="gg-card__title">Urgent</p>
            <p className="gg-card__meta">{stats.urgent}</p>
            <StatPill label="Priority" value={stats.urgent} tone={stats.urgent > 0 ? 'error' : 'info'} />
          </article>
        </TemplateSummaryRow>
      </TemplateHeaderBlock>

      {unavailableMessage ? (
        <TemplateSectionCard
          label="Announcements"
          title="Announcements data source unavailable"
          subtitle="This environment is missing required database objects for announcements."
        >
          <p className="gg-inline-alert gg-inline-alert--warning">{unavailableMessage}</p>
          <EmptyState
            title="Announcements data source unavailable"
            message="Announcements table is not currently available in this environment."
          />
        </TemplateSectionCard>
      ) : (
        <>
          {composerOpen ? (
            <TemplateSectionCard
              label="Compose"
              title="Publish Announcement"
              subtitle="Create clear operational messages with audience and priority tags."
            >
              <TemplateFormShell>
                <form className="gg-form-grid gg-form-grid--split" onSubmit={handleSubmit}>
                  <label className="gg-form-field gg-form-field--wide">
                    <span>Title *</span>
                    <input
                      className="gg-input"
                      value={form.values.title}
                      onChange={(event) => form.setField('title', event.target.value)}
                      placeholder="Announcement title"
                    />
                  </label>
                  <label className="gg-form-field gg-form-field--wide">
                    <span>Body</span>
                    <textarea
                      className="gg-textarea"
                      value={form.values.body}
                      onChange={(event) => form.setField('body', event.target.value)}
                      placeholder="Add details for workers, managers, or site teams."
                    />
                  </label>
                  <label className="gg-form-field">
                    <span>Audience</span>
                    <select
                      className="gg-select"
                      value={form.values.audience}
                      onChange={(event) => form.setField('audience', event.target.value)}
                    >
                      {AUDIENCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="gg-form-field">
                    <span>Priority</span>
                    <select
                      className="gg-select"
                      value={form.values.priority}
                      onChange={(event) => form.setField('priority', event.target.value)}
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="gg-form-field">
                    <span>Site ID (optional)</span>
                    <input
                      className="gg-input"
                      value={form.values.site_id}
                      onChange={(event) => form.setField('site_id', event.target.value)}
                      placeholder="site_123"
                    />
                  </label>
                  <label className="gg-form-field">
                    <span>Active</span>
                    <input
                      type="checkbox"
                      checked={form.values.is_active !== false}
                      onChange={(event) => form.setField('is_active', event.target.checked)}
                    />
                  </label>
                  <div className="gg-form-actions gg-form-field--wide">
                    <button type="submit" className="gg-button gg-button--primary" disabled={formSubmit.isSubmitting}>
                      {formSubmit.isSubmitting ? 'Publishing...' : 'Publish Announcement'}
                    </button>
                    <button type="button" className="gg-button gg-button--secondary" onClick={() => form.reset()} disabled={formSubmit.isSubmitting}>
                      Reset Form
                    </button>
                  </div>
                </form>
              </TemplateFormShell>

              {formMessage.message ? <p className={`gg-inline-alert ${formMessage.tone}`}>{formMessage.message}</p> : null}
            </TemplateSectionCard>
          ) : null}

          <TemplateSectionCard
            label="Directory"
            title="Announcement History"
            subtitle="Track published communications and quickly toggle visibility."
          >
            <FilterBar
              left={(
                <label className="gg-form-field">
                  <span>Audience</span>
                  <select className="gg-select" value={audienceFilter} onChange={(event) => setAudienceFilter(event.target.value)}>
                    <option value="all">All audiences</option>
                    {AUDIENCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              right={(
                <label className="gg-form-field">
                  <span>Status</span>
                  <select className="gg-select" value={activityFilter} onChange={(event) => setActivityFilter(event.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="active">Active only</option>
                    <option value="inactive">Inactive only</option>
                  </select>
                </label>
              )}
              summary={`${visibleAnnouncements.length} announcements shown`}
            />

            {visibleAnnouncements.length === 0 ? (
              <EmptyState
                title={announcements.length === 0 ? 'No announcements' : 'No announcements in this filter'}
                message={announcements.length === 0 ? 'Publish a notice to start communication history.' : 'Adjust filters to view announcements.'}
                action={(
                  <button type="button" className="gg-button gg-button--primary" onClick={() => setComposerOpen(true)}>
                    Create Announcement
                  </button>
                )}
              />
            ) : (
              <TemplateTableShell>
                <div className="gg-record-list">
                  {visibleAnnouncements.map((announcement) => {
                    const isActive = announcement.is_active !== false
                    const audience = String(announcement.audience || 'team').toLowerCase()
                    const priority = String(announcement.priority || 'normal').toLowerCase()
                    return (
                      <article key={announcement.id} className="gg-record-item">
                        <div className="gg-record-item__head">
                          <div>
                            <p className="gg-record-item__title">{announcement.title || 'Untitled'}</p>
                            <p className="gg-record-item__meta">
                              {announcement.body || 'No body content.'}
                            </p>
                            <p className="gg-record-item__meta">
                              Audience {formatTitleText(audience, 'Team')} • Site {announcement.site_id || 'All sites'} • Updated {formatDateTime(announcement.updated_at || announcement.created_at)}
                            </p>
                          </div>
                          <ActionButtonRow>
                            <StatusBadge status={toPriorityStatus(priority)} label={formatTitleText(priority, 'Normal')} />
                            <StatusBadge status={isActive ? 'success' : 'paused'} label={isActive ? 'Active' : 'Inactive'} />
                            <button
                              type="button"
                              className="gg-button gg-button--ghost"
                              onClick={() => handleToggleActive(announcement)}
                              disabled={toggleBusyId === String(announcement.id)}
                            >
                              {toggleBusyId === String(announcement.id) ? 'Saving...' : isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </ActionButtonRow>
                        </div>
                      </article>
                    )
                  })}
                </div>
              </TemplateTableShell>
            )}
          </TemplateSectionCard>
        </>
      )}
    </TemplatePage>
  )
}
